import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdClose, MdArrowBack, MdArrowForward } from 'react-icons/md';
import axios from 'config/axios';
import { useAppSelector } from 'hooks/useStore';
import useStudySession from 'hooks/useStudySession';
import Backdrop from 'components/Backdrop';
import Button from 'components/Button';
import Loader from 'components/Loader';

import './index.css';

type Question = {
    id: number;
    wordId: number;
    sentence: string;
    choices: string[];
};

const CHOICE_LETTERS = ['A', 'B', 'C', 'D'];

function VocabQuiz() {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [quizId, setQuizId] = useState<number | null>(null);
    const [attempt, setAttempt] = useState(1);
    const [testNumber, setTestNumber] = useState(1);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showExitPopup, setShowExitPopup] = useState(false);
    const [saving, setSaving] = useState(false);
    const [discarding, setDiscarding] = useState(false);
    const [focusedChoice, setFocusedChoice] = useState(-1);
    const [slideDir, setSlideDir] = useState<'left' | 'right' | null>(null);

    const { vocabSetId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { organization } = useAppSelector(state => state.organization);

    // Refs for keyboard handler to read latest state
    const currentIndexRef = useRef(0);
    const questionsRef = useRef<Question[]>([]);
    const answersRef = useRef<Record<string, string>>({});
    const focusedChoiceRef = useRef(-1);
    const showExitPopupRef = useRef(false);
    const submittingRef = useRef(false);

    useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
    useEffect(() => { questionsRef.current = questions; }, [questions]);
    useEffect(() => { answersRef.current = answers; }, [answers]);
    useEffect(() => { focusedChoiceRef.current = focusedChoice; }, [focusedChoice]);
    useEffect(() => { showExitPopupRef.current = showExitPopup; }, [showExitPopup]);
    useEffect(() => { submittingRef.current = submitting; }, [submitting]);

    const resumeQuizId = (location.state as any)?.resumeQuizId;
    const studentVocabSetId = (location.state as any)?.studentVocabSetId;

    useStudySession({
        activityType: 'vocab_quiz',
        activityIds: { studentVocabTestId: quizId ?? undefined },
        enabled: quizId !== null,
    });

    useEffect(() => {
        let cancelled = false;

        if (resumeQuizId) {
            axios.get(`vocab/quiz/${resumeQuizId}/`)
                .then(res => {
                    if (cancelled) return;
                    setQuizId(res.data.quizId);
                    setAttempt(res.data.attempt);
                    setTestNumber(res.data.testNumber);
                    setQuestions(res.data.questions);
                    setAnswers(res.data.savedAnswers || {});
                    const saved = res.data.savedAnswers || {};
                    const firstUnanswered = res.data.questions.findIndex(
                        (q: Question) => !saved[String(q.id)]
                    );
                    setCurrentIndex(firstUnanswered >= 0 ? firstUnanswered : 0);
                    setLoading(false);
                })
                .catch(error => {
                    if (cancelled) return;
                    console.error('Failed to resume quiz:', error?.response?.data || error);
                    navigate('/vocab');
                });
        } else if (studentVocabSetId) {
            axios.post('vocab/quiz/start/', { studentVocabSetId })
                .then(res => {
                    if (cancelled) return;
                    setQuizId(res.data.quizId);
                    setAttempt(res.data.attempt);
                    setTestNumber(res.data.testNumber);
                    setQuestions(res.data.questions);
                    setAnswers(res.data.savedAnswers || {});
                    setLoading(false);
                })
                .catch(error => {
                    if (cancelled) return;
                    // 409 = in-progress attempt exists (likely from Strict Mode double-fire)
                    // Resume it instead of navigating away
                    if (error?.response?.status === 409) {
                        axios.get(`vocab/quiz/sets/${organization.urlSlug}/`)
                            .then(setsRes => {
                                if (cancelled) return;
                                const sets = setsRes.data.sets || [];
                                const matchingSet = sets.find(s => s.studentVocabSetId === studentVocabSetId);
                                if (matchingSet?.inProgressAttempt) {
                                    return axios.get(`vocab/quiz/${matchingSet.inProgressAttempt.id}/`);
                                }
                                throw new Error('No in-progress attempt found');
                            })
                            .then(res => {
                                if (cancelled) return;
                                setQuizId(res.data.quizId);
                                setAttempt(res.data.attempt);
                                setTestNumber(res.data.testNumber);
                                setQuestions(res.data.questions);
                                setAnswers(res.data.savedAnswers || {});
                                setLoading(false);
                            })
                            .catch(() => {
                                if (!cancelled) navigate('/vocab');
                            });
                    } else {
                        console.error('Failed to start quiz:', error?.response?.data || error);
                        navigate('/vocab');
                    }
                });
        } else {
            navigate('/vocab');
        }

        return () => { cancelled = true; };
    }, []);

    // Reset focused choice when question changes
    useEffect(() => {
        setFocusedChoice(-1);
        setSlideDir(null);
    }, [currentIndex]);

    const selectAnswer = (choice: string) => {
        const q = questionsRef.current[currentIndexRef.current];
        if (!q) return;
        setAnswers(prev => ({ ...prev, [String(q.id)]: choice }));
    };

    const goNext = () => {
        if (currentIndexRef.current < questionsRef.current.length - 1) {
            setSlideDir('left');
            setTimeout(() => setCurrentIndex(currentIndexRef.current + 1), 0);
        }
    };

    const goPrev = () => {
        if (currentIndexRef.current > 0) {
            setSlideDir('right');
            setTimeout(() => setCurrentIndex(currentIndexRef.current - 1), 0);
        }
    };

    const handleSubmit = () => {
        if (!quizId || submittingRef.current) return;
        setSubmitting(true);
        axios.post('vocab/quiz/submit/', { quizId, answers: answersRef.current })
            .then(() => navigate(`/vocab/quiz/result/${quizId}`, { replace: true }))
            .catch(() => setSubmitting(false));
    };

    const handleSaveAndExit = () => {
        if (!quizId) return;
        setSaving(true);
        axios.post('vocab/quiz/save/', { quizId, answers: answersRef.current })
            .then(() => navigate(`/vocab/flashcards/${vocabSetId}`, { replace: true }))
            .catch(() => setSaving(false));
    };

    const handleDiscard = () => {
        if (!quizId) return;
        setDiscarding(true);
        axios.post('vocab/quiz/discard/', { quizId })
            .then(() => navigate(`/vocab/flashcards/${vocabSetId}`, { replace: true }))
            .catch(() => setDiscarding(false));
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (showExitPopupRef.current) return;

            const q = questionsRef.current[currentIndexRef.current];
            if (!q) return;

            switch (e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    goNext();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    goPrev();
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    setFocusedChoice(prev => Math.min(prev + 1, q.choices.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setFocusedChoice(prev => Math.max(prev - 1, 0));
                    break;
                case 'Enter': {
                    e.preventDefault();
                    const fc = focusedChoiceRef.current;
                    if (fc >= 0 && fc < q.choices.length) {
                        selectAnswer(q.choices[fc]);
                        // Auto-advance after selecting
                        if (currentIndexRef.current < questionsRef.current.length - 1) {
                            setTimeout(goNext, 200);
                        }
                    }
                    break;
                }
                default:
                    if (['a','b','c','d','A','B','C','D'].includes(e.key)) {
                        const idx = e.key.toUpperCase().charCodeAt(0) - 65;
                        if (q.choices[idx]) {
                            setFocusedChoice(idx);
                            selectAnswer(q.choices[idx]);
                        }
                    }
                    break;
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, []);

    if (loading) return <Loader />;

    const currentQuestion = questions[currentIndex];
    const totalQuestions = questions.length;
    const answeredCount = Object.keys(answers).length;
    const selectedAnswer = currentQuestion ? answers[String(currentQuestion.id)] : null;
    const allAnswered = answeredCount === totalQuestions;

    return (
        <div className='vq'>
            <Helmet>
                <title>Vocab Quiz - {organization.name}</title>
            </Helmet>

            {/* Header */}
            <div className='vq__header'>
                <button className='vq__exit' onClick={() => setShowExitPopup(true)}>
                    <MdClose />
                    <span>Exit</span>
                </button>
                <div className='vq__headerCenter'>
                    <span className='vq__badge'>Quiz {testNumber}</span>
                    <div className='vq__progressBar'>
                        <div
                            className='vq__progressFill'
                            style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
                        />
                    </div>
                    <span className='vq__progressText'>{answeredCount}/{totalQuestions}</span>
                </div>
                <div className='vq__headerRight'>
                    <span className='vq__questionCounter'>
                        {currentIndex + 1} of {totalQuestions}
                    </span>
                </div>
            </div>

            {/* Question area */}
            {currentQuestion && (
                <div className='vq__content'>
                    {/* Left arrow */}
                    <button
                        className={`vq__arrow vq__arrow--left ${currentIndex === 0 ? 'vq__arrow--disabled' : ''}`}
                        onClick={goPrev}
                        disabled={currentIndex === 0}
                    >
                        <MdArrowBack />
                    </button>

                    {/* Main card */}
                    <div className='vq__card' key={currentIndex}>
                        <div className='vq__sentence'>
                            <span className='vq__questionNumber'>{currentIndex + 1}.</span> {currentQuestion.sentence}
                        </div>

                        <div className='vq__choices'>
                            {currentQuestion.choices.map((choice, i) => {
                                const isSelected = selectedAnswer === choice;
                                const isFocused = focusedChoice === i;
                                return (
                                    <button
                                        key={i}
                                        className={`vq__choice ${isSelected ? 'vq__choice--selected' : ''} ${isFocused && !isSelected ? 'vq__choice--focused' : ''}`}
                                        onClick={() => selectAnswer(choice)}
                                        onMouseEnter={() => setFocusedChoice(i)}
                                    >
                                        <span className={`vq__choiceLetter ${isSelected ? 'vq__choiceLetter--selected' : ''}`}>
                                            {CHOICE_LETTERS[i]}
                                        </span>
                                        <span className='vq__choiceText'>{choice}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Submit on last question */}
                        {currentIndex === totalQuestions - 1 && (
                            <button
                                className={`vq__submit ${!allAnswered || submitting ? 'vq__submit--disabled' : ''}`}
                                onClick={handleSubmit}
                                disabled={!allAnswered || submitting}
                            >
                                {submitting ? 'Submitting...' : allAnswered ? 'Submit Quiz' : `${totalQuestions - answeredCount} unanswered`}
                            </button>
                        )}
                    </div>

                    {/* Right arrow */}
                    <button
                        className={`vq__arrow vq__arrow--right ${currentIndex === totalQuestions - 1 ? 'vq__arrow--disabled' : ''}`}
                        onClick={goNext}
                        disabled={currentIndex === totalQuestions - 1}
                    >
                        <MdArrowForward />
                    </button>
                </div>
            )}

            {/* Question dots */}
            <div className='vq__dots'>
                {questions.map((q, i) => (
                    <button
                        key={q.id}
                        className={`vq__dot ${i === currentIndex ? 'vq__dot--current' : ''} ${answers[String(q.id)] ? 'vq__dot--answered' : ''}`}
                        onClick={() => setCurrentIndex(i)}
                    />
                ))}
            </div>

            {/* Keyboard hint */}
            <div className='vq__hint'>
                <span><kbd>←</kbd><kbd>→</kbd> navigate</span>
                <span><kbd>↑</kbd><kbd>↓</kbd> browse</span>
                <span><kbd>Enter</kbd> select</span>
                <span><kbd>A</kbd>-<kbd>D</kbd> quick pick</span>
            </div>

            {/* Exit popup */}
            {showExitPopup && (
                <>
                    <Backdrop />
                    <div className='vq__popup'>
                        <h2 className='vq__popupTitle'>Leave Quiz?</h2>
                        <p className='vq__popupText'>
                            You have answered {answeredCount} of {totalQuestions} questions.
                        </p>
                        <div className='vq__popupActions'>
                            <Button
                                onClick={handleSaveAndExit}
                                disabled={saving || discarding}
                                fullWidth
                            >
                                {saving ? 'Saving...' : 'Save & Exit'}
                            </Button>
                            <Button
                                onClick={handleDiscard}
                                disabled={saving || discarding}
                                secondary
                                fullWidth
                            >
                                {discarding ? 'Discarding...' : 'Discard Attempt'}
                            </Button>
                            <button
                                className='vq__popupCancel'
                                onClick={() => setShowExitPopup(false)}
                                disabled={saving || discarding}
                            >
                                Continue Quiz
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default VocabQuiz;
